import _get from "lodash/get";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import { getExerciseWeek } from "../../../../utils/cycle-helpers";
import {
  CardsSkeleton,
  SlotExerciseList,
} from "../../common-components/ProgramSlotCard";
import { splitSections } from "../../../../utils/section-types";
import WorkoutNoteBlock from "../../../athlete-portal/calendar/components/EditMode/ExerciseEditor/WorkoutNoteBlock";

/** Last calendar day of the team week block (startDate + 6 days), YYYYMMDD */
const getProgramWeekEndDate = (program) =>
  Number(
    moment(program.startDate, "YYYYMMDD").add(6, "days").format("YYYYMMDD")
  );

/**
 * Teams tabs: History vs Upcoming (main) vs Upcoming (second list = later months).
 * Uses week range so blocks that cross months are not misclassified by start month alone.
 */
const monthFilter = (program, weekFilter) => {
  const programDate = moment(program.startDate, "YYYYMMDD");
  const now = moment();
  const today = Number(moment().format("YYYYMMDD"));
  const weekEndDate = getProgramWeekEndDate(program);

  if (weekFilter === "history") {
    return weekEndDate < today;
  }
  if (weekFilter === "this-month") {
    return weekEndDate >= today && !programDate.isAfter(now, "month");
  }
  if (weekFilter === "upcoming") {
    return programDate.isAfter(now, "month");
  }
};

const TeamProgramManager = ({ locals, api }) => {
  const { t } = useTranslation("coaching-portal");
  const { withWeight, fromMetric, activeUserToRequest, query } = locals;

  const [exerciseWeek, setExerciseWeek] = useState(null);
  const [cycleExercises, setCycleExercises] = useState(null);

  const cycle = locals?.editCycle;

  const [sortedPrograms, setSortedPrograms] = useState([]);
  const [weekFilter, setWeekFilter] = useState("this-month");
  const [teamWeeks, setTeamWeeks] = useState(null);
  const [teamWeeksUpcoming, setTeamWeeksUpcoming] = useState(null);
  const [teamWeekExercises, setTeamWeekExercises] = useState(null);
  const [teamWeekSections, setTeamWeekSections] = useState({});
  const [loading, setLoading] = useState(null);
  const [lastFoundHere, setLastFoundHere] = useState(false);

  let organizationQuery = ``;

  if (query.organizationCoach) {
    organizationQuery = `&organizationCoach=${query.organizationCoach}`;
  }

  const fromTemplates =
    (locals?.pathname || locals?.path || "").includes("/templates/teams") ||
    query?.fromTemplate === "true";
  const editProgramBase = fromTemplates
    ? "/coaching-portal/templates/edit-program"
    : "/coaching-portal/store/edit-program";

  const currentDateNumber = Number(moment().format("YYYYMMDD"));

  const programThisWeek =
    cycle?.programs?.find((prog) => {
      const progEndDate = getProgramWeekEndDate(prog);
      return (
        currentDateNumber >= prog?.startDate && currentDateNumber <= progEndDate
      );
    }) || null;

  const currentProgramThreshold = programThisWeek?.startDate;

  useEffect(() => {
    if (cycle) {
      const sortedPrograms = [...cycle.programs].sort(
        (a, b) => a.startDate - b.startDate
      );

      setSortedPrograms(sortedPrograms);
    }
  }, [cycle]);

  useEffect(() => {
    if (sortedPrograms.length > 0) {
      const groupByWeek = sortedPrograms.filter((program) =>
        monthFilter(program, weekFilter)
      );

      setTeamWeeks(groupByWeek);
      let groupUpcoming = [];
      if (weekFilter === "this-month") {
        groupUpcoming = sortedPrograms.filter((program) =>
          monthFilter(program, "upcoming")
        );
        setTeamWeeksUpcoming(groupUpcoming);
      } else {
        setTeamWeeksUpcoming([]);
      }

      // find the last index of the last filtered item in the full sorted array
      if (groupByWeek.length > 0) {
        const lastProgram = groupByWeek[groupByWeek.length - 1];
        const lastIndex = sortedPrograms.findIndex(
          (p) => p.order === lastProgram.order
        );

        const hasMoreAfter = lastIndex < sortedPrograms.length - 1;
        setLastFoundHere(!hasMoreAfter);
      } else {
        setLastFoundHere(false);
      }
    }
  }, [sortedPrograms, weekFilter]);

  useEffect(() => {
    const requestWeek = async () => {
      setLoading(exerciseWeek);
      const response = await getExerciseWeek(api, cycle._id, exerciseWeek);
      setCycleExercises(response);
    };

    if (exerciseWeek) {
      requestWeek();
    }
  }, [exerciseWeek]);

  useEffect(() => {
    if (cycleExercises && exerciseWeek) {
      const { exercises, sections } = cycleExercises;

      // cycleExercises returns an object with the keys being the weeks of the program, so we just need the first week as teams only has 1 week programs
      const newExerciseWeek = exercises["1"];

      if (teamWeekExercises) {
        setTeamWeekExercises({
          ...teamWeekExercises,
          [exerciseWeek]: newExerciseWeek,
        });
      } else {
        setTeamWeekExercises({ [exerciseWeek]: newExerciseWeek });
      }

      setTeamWeekSections((prev) => ({
        ...prev,
        [exerciseWeek]: sections || [],
      }));

      setCycleExercises(null);
      setLoading(null);
    }
  }, [cycleExercises, exerciseWeek]);

  const title = {
    history: t("teams.programManager.history"),
    "this-month": t("teams.programManager.thisMonth"),
  };

  return (
    <div>
      <hgroup className="history-filter-buttons">
        <button
          data-button-type={`${
            weekFilter === "history" ? "outline" : "secondary"
          }`}
          onClick={() => {
            setWeekFilter("history");
          }}
        >
          {t("teams.programManager.history")}
        </button>
        <button
          data-button-type={`${
            weekFilter === "this-month" ? "outline" : "secondary"
          }`}
          onClick={() => {
            setWeekFilter("this-month");
          }}
        >
          {t("teams.programManager.upcoming")}
        </button>
      </hgroup>

      <TeamsWeekTable
        cycle={cycle}
        teamWeeks={teamWeeks}
        hasFutureWeeks={teamWeeksUpcoming && teamWeeksUpcoming.length > 0}
        weekFilter={weekFilter}
        organizationQuery={organizationQuery}
        teamWeekExercises={teamWeekExercises}
        teamWeekSections={teamWeekSections}
        withWeight={withWeight}
        fromMetric={fromMetric}
        setExerciseWeek={setExerciseWeek}
        loading={loading}
        currentProgramThreshold={currentProgramThreshold}
        lastFoundHere={lastFoundHere}
        activeUserToRequest={activeUserToRequest}
        sortedPrograms={sortedPrograms}
        title={title[weekFilter]}
        editProgramBase={editProgramBase}
        fromTemplates={fromTemplates}
        programThisWeek={programThisWeek}
      />

      {teamWeeksUpcoming && teamWeeksUpcoming.length > 0 && (
        <TeamsWeekTable
          cycle={cycle}
          teamWeeks={teamWeeksUpcoming}
          weekFilter={weekFilter}
          hasFutureWeeks={false}
          organizationQuery={organizationQuery}
          teamWeekExercises={teamWeekExercises}
          teamWeekSections={teamWeekSections}
          withWeight={withWeight}
          fromMetric={fromMetric}
          setExerciseWeek={setExerciseWeek}
          loading={loading}
          currentProgramThreshold={currentProgramThreshold}
          lastFoundHere={true}
          activeUserToRequest={activeUserToRequest}
          sortedPrograms={sortedPrograms}
          title={t("teams.programManager.upcoming")}
          editProgramBase={editProgramBase}
          fromTemplates={fromTemplates}
          programThisWeek={programThisWeek}
        />
      )}
    </div>
  );
};

const TeamsWeekTable = ({
  teamWeeks,
  weekFilter,
  cycle,
  organizationQuery,
  teamWeekExercises,
  teamWeekSections,
  withWeight,
  fromMetric,
  setExerciseWeek,
  loading,
  currentProgramThreshold,
  lastFoundHere,
  sortedPrograms,
  activeUserToRequest,
  hasFutureWeeks,
  title,
  editProgramBase,
  fromTemplates,
  programThisWeek,
}) => {
  const { t } = useTranslation("coaching-portal");
  const intensityMeasure =
    _get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";
  const units = activeUserToRequest?.units;
  const webAppTheme = activeUserToRequest?.webAppTheme || "dark";

  if (!teamWeeks?.length) {
    if (
      weekFilter === "history" ||
      (weekFilter === "this-month" && !hasFutureWeeks)
    ) {
      return <TeamsTableEmpty title={title} />;
    }
    return null;
  }

  return (
    <>
      <h3 className="mt-6">{title}</h3>

      <div className="program-container">
        {teamWeeks && teamWeeks.length > 0 && (
          <div className="grid grid-cols-6 px-6 py-6 items-center">
            <p className="s1">{t("teams.programManager.weeks")}</p>
            <p className="s1">{t("teams.programManager.name")}</p>
            <p className="s1">{t("teams.programManager.description")}</p>
            <p className="s1">{t("teams.programManager.workouts")}</p>
            <p className="s1">{t("teams.programManager.trainingSplit")}</p>
            <p className="s1 text-right">{t("teams.programManager.action")}</p>
          </div>
        )}

        <div className="programs">
          {teamWeeks &&
            teamWeeks.length > 0 &&
            teamWeeks.map((program) => (
              <TeamWeekRow
                key={`${program.startDate}-program-${program.order}`}
                program={program}
                cycle={cycle}
                organizationQuery={organizationQuery}
                teamWeekExercises={teamWeekExercises}
                teamWeekSections={teamWeekSections}
                withWeight={withWeight}
                fromMetric={fromMetric}
                intensityMeasure={intensityMeasure}
                isRiR={isRiR}
                units={units}
                setExerciseWeek={setExerciseWeek}
                loading={loading}
                webAppTheme={webAppTheme}
                currentProgramThreshold={currentProgramThreshold}
                lastFoundHere={lastFoundHere}
                lastIndex={Math.max(
                  ...sortedPrograms.map((program) => Number(program.order))
                )}
                editProgramBase={editProgramBase}
                programThisWeek={programThisWeek}
              />
            ))}
        </div>

        {teamWeeks &&
          teamWeeks.length > 0 &&
          weekFilter !== "history" &&
          !hasFutureWeeks && (
            <div
              className="add-new-week"
              onClick={() => {
                submit(`/actions/cycle/${cycle._id}/teams-add-week`, {
                  method: "POST",
                  body: { fromTemplates },
                });
              }}
            >
              <div className="add-new-week-button">
                <SvgIcon src={"/img/new-icons/add.svg"} />
              </div>
              <p>{t("teams.programManager.addNewWeek")}</p>
            </div>
          )}
      </div>
    </>
  );
};

const TeamsTableEmpty = ({ title }) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <>
      <h3 className="mt-6">{title}</h3>

      <div className="program-container">
        <div className="no-data-state">
          <p className="s1">{t("teams.programManager.noWeeksForPeriod")}</p>
          <p className="p4 opacity-70 w-48 text-center">
            {t("teams.programManager.addNewHint")}
          </p>
        </div>
      </div>
    </>
  );
};

const TeamWeekRow = ({
  program,
  cycle,
  organizationQuery,
  teamWeekExercises,
  teamWeekSections,
  fromMetric,
  withWeight,
  intensityMeasure,
  isRiR,
  units,
  setExerciseWeek,
  loading,
  webAppTheme,
  currentProgramThreshold,
  lastFoundHere,
  lastIndex,
  editProgramBase,
  programThisWeek,
}) => {
  const { t } = useTranslation("coaching-portal");
  const [isOpen, setIsOpen] = useState(false);
  const backgroundColor = webAppTheme === "dark" ? "#363C43" : "#F1F3F6";
  const foregroundColor = webAppTheme === "dark" ? "#40464D" : "#E0E4EA";
  const currentDateNumber = Number(moment().format("YYYYMMDD"));

  // Determine what to show
  let label;

  if (!currentProgramThreshold) {
    // No program for the current week → could be because all programs are in the future or none exist
    const hasFuturePrograms = cycle?.programs?.some(
      (p) => p.startDate > currentDateNumber
    );

    label = hasFuturePrograms ? "Edit" : "View";
  } else if (currentProgramThreshold <= program?.startDate) {
    label = "Edit";
  } else {
    label = "View";
  }

  useEffect(() => {
    if (isOpen && teamWeekExercises && !!teamWeekExercises[program.order]) {
      setExerciseListHeight();
    }
  }, [isOpen, teamWeekExercises]);

  const setExerciseListHeight = () => {
    const className = `workout-${program.order}-card`;
    // gets one row of workouts
    const elements = Array.from(document.getElementsByClassName(className));

    elements.forEach((el) => {
      el.style.height = "auto";
    });
    const elementsHeights = elements.map((el) => el.clientHeight);
    const maxHeight = Math.max(...elementsHeights);
    // set the height of all elements in the row to the same height as the tallest element in it
    elements.forEach((el) => {
      el.style.height = `${maxHeight}px`;
    });
  };

  const trainingSplit = program.sessions
    .map((session) => session.workoutDay || 0)
    .sort((a, b) => a - b)
    .map((day) => moment().day(day).format("dd"))
    .join(", ");

  const weekSplit = Array.from(
    { length: 7 },
    (_, i) => ((cycle.firstDayOfWeek || 0) + i) % 7
  );

  return (
    <div key={`${program.startDate}-program-${program.order}`}>
      <div
        className={`program-row ${isOpen ? "rounded-b-none!" : ""}`}
        onClick={(e) => {
          const row = e.currentTarget; // this specific row
          const clickedLink = e.target.closest("a");

          if (clickedLink && row.contains(clickedLink)) return;

          setIsOpen(!isOpen);
          setExerciseWeek(program.order);
        }}
      >
        <div>
          <SvgIcon
            src={`/img/new-icons/arrow-square-right.svg`}
            classNames={isOpen ? "turned" : ""}
          />
          <div className="flex flex-col gap-2">
            <p>
              {moment(program.startDate, "YYYYMMDD").format("MMM D, YYYY")} -{" "}
              {moment(
                String(getProgramWeekEndDate(program)),
                "YYYYMMDD"
              ).format("MMM D, YYYY")}
            </p>
            {programThisWeek &&
              program.startDate === programThisWeek.startDate && (
                <span className="active-week-indicator">
                  {t("teams.programManager.activeWeek")}
                </span>
              )}
          </div>
        </div>
        <div>
          <p>{program.name || ""}</p>
        </div>
        <div>
          <p>{program.description || "-"}</p>
        </div>
        <div>
          <p>{program.sessions.length}</p>
        </div>
        <div className="flex flex-col! items-start! gap-2">
          <p>{trainingSplit}</p>
          {program.trainingSplitType === "forced" && (
            <span className="forced-split-indicator">
              <SvgIcon
                classNames="forced-split-indicator-icon"
                src="/img/new-icons/lock-closed.svg"
              />
              Forced
            </span>
          )}
        </div>
        <div className="row-buttons z-10">
          {!!lastFoundHere && lastIndex === program.order && (
            <a
              role="button"
              data-button-type="secondary"
              data-icon
              data-icon-only
              className="edit"
              href={`/actions/cycle/program/duplicate/${cycle._id}?program=${program.order}`}
            >
              <SvgIcon
                className="yellow"
                src="/img/new-icons/duplicate.svg"
                alt={t("teams.programManager.editAlt")}
              />
            </a>
          )}
          {program.video && (
            <a
              role="button"
              data-button-type="secondary"
              data-icon
              data-icon-only
              className="edit"
              href={`/actions/refresh?modal[type]=week-video&video=${program.video}`}
            >
              <SvgIcon
                className="yellow"
                src="/img/new-icons/play-white.svg"
                alt={t("teams.programManager.editAlt")}
              />
            </a>
          )}
          <a
            role="button"
            data-button-type="secondary"
            data-icon
            data-icon-only
            className="edit"
            href={`/actions/refresh?modal[type]=week-settings&program=${program.order}`}
          >
            <SvgIcon
              className="yellow"
              src="/img/new-icons/settings.svg"
              alt={t("teams.programManager.editAlt")}
            />
          </a>

          {/* Only delete the last one and only weeks after the last one xd */}
          {!!lastFoundHere &&
            lastIndex === program.order &&
            program.order !== 1 &&
            label !== "View" && (
              <a
                role="button"
                data-button-type="secondary"
                data-icon
                data-icon-only
                className="edit"
                href={`/actions/cycle/delete-program/${cycle._id}/${
                  program.order
                }/?isFromCoachingPortal=${false}&isFromTeams=${true}`}
              >
                <SvgIcon src="/img/new-icons/red-trash.svg" noFilterOnLight />
              </a>
            )}
          <a
            role="button"
            data-button-type="outline"
            href={`${editProgramBase}/${cycle._id}?program=${
              program.order
            }&storeType=teams${organizationQuery}${label === "Edit" ? "" : "&readOnly=true"}`}
          >
            {label === "Edit" ? (
              <p>&nbsp;{t("teams.programManager.edit")}&nbsp;</p>
            ) : (
              <p>{t("teams.programManager.view")}</p>
            )}
          </a>
        </div>
      </div>

      {isOpen &&
        (teamWeekExercises && teamWeekExercises[program.order] ? (
          <article className={`workout-${program.order}-list`}>
            {weekSplit.map((dayNumber, index) => {
              const sessionNumber = program.sessions?.find(
                (workout) => workout.workoutDay === dayNumber
              )?.order;

              const session = sessionNumber
                ? teamWeekExercises[program.order][sessionNumber]
                : null;

              // Read-only section separators for this session (text-only
              // dividers). Teams are single-week programs, so scope to week 1.
              const sessionSections = sessionNumber
                ? (teamWeekSections?.[program.order] || []).filter(
                    (s) =>
                      s.cycle &&
                      s.cycle.programWeek === 1 &&
                      s.cycle.programSession === sessionNumber
                  )
                : [];

              // The workout note renders separately, above the session's cards.
              const { sections: normalSessionSections, note: sessionNote } =
                splitSections(sessionSections);

              const isForcedPrescribed =
                program.trainingSplitType === "forced" && !!sessionNumber;

              return (
                <div
                  key={`${dayNumber}-${program.startDate}-workout-day`}
                  className={`exercise-card workout-${program.order}-card${
                    isForcedPrescribed ? " forced-prescribed-day" : ""
                  }`}
                >
                  <div className="flex flex-row items-center gap-2 ml-2 mt-1">
                    <p className="s1 ml-2 mt-1">
                      {t("teams.programManager.dayLabel", {
                        index: index + 1,
                        dayName: moment().day(dayNumber).format("dddd"),
                      })}
                    </p>
                    {isForcedPrescribed && (
                      <ToolTip
                        id={`forced-split-lock-${program.order}-${dayNumber}`}
                        body="Members will have this workout forced on this day."
                      >
                        <SvgIcon
                          classNames="forced-split-lock"
                          src="/img/new-icons/lock-closed.svg"
                        />
                      </ToolTip>
                    )}
                  </div>

                  <WorkoutNoteBlock note={sessionNote} editable={false} />

                  <SlotExerciseList
                    session={session}
                    sessionSections={normalSessionSections}
                    keyPrefix={`${program.order}-${dayNumber}`}
                    withWeight={withWeight}
                    fromMetric={fromMetric}
                    intensityMeasure={intensityMeasure}
                    isRiR={isRiR}
                    units={units}
                  />
                </div>
              );
            })}
          </article>
        ) : loading !== program.order ? (
          <article>
            <section className="no-data-state">
              <p className="s1">{t("teams.programManager.noDataToShow")}</p>
              <p className="p4 opacity-70 w-48 text-center">
                {t("teams.programManager.editHint")}
              </p>
            </section>
          </article>
        ) : (
          <article>
            <section className="loading">
              <CardsSkeleton
                backgroundColor={backgroundColor}
                foregroundColor={foregroundColor}
              />
            </section>
          </article>
        ))}
    </div>
  );
};

export default TeamProgramManager;
